import {useEffect, useMemo, useState} from 'react'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {X} from 'lucide-react'
import {searchTopicPromotions, type TopicPromotion} from '../services/topicsApi'

type Props = {
  topicId: string
  label: string
  value: TopicPromotion[]
  onChange: (promotions: TopicPromotion[]) => void
  required?: boolean
  error?: string
  listOnly?: boolean
  multiple?: boolean
  disabled?: boolean
  disallowSuggestedList?: boolean
}

export function PromotionSummary({promotion}: {promotion: TopicPromotion}) {
  return (
    <>
      <Typography
        variant='subtitle2'
        fontWeight={600}
        sx={{pr: 3.5, overflowWrap: 'anywhere', lineHeight: 1.5}}
      >
        {promotion.name}
      </Typography>
      <Box
        sx={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5, mt: 1.5}}
      >
        {[
          ['ID Promoção', promotion.promotionId || 'Não informado'],
          ['Número do card na promoção', promotion.cardNumber ?? 'Não informado'],
          ['Tipo de card', promotion.cardType || 'Não informado'],
          ['Origem', 'Briefing de Promoções'],
        ].map(([label, value]) => (
          <Box key={String(label)} sx={{minWidth: 0}}>
            <Typography variant='caption' color='text.secondary'>
              {label}
            </Typography>
            <Typography variant='body2' fontWeight={500} sx={{overflowWrap: 'anywhere'}}>
              {String(value)}
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  )
}

export default function TopicPromotionPicker({
  topicId,
  label,
  value,
  onChange,
  required = true,
  error,
  listOnly = false,
  multiple = true,
  disabled = false,
  disallowSuggestedList = false,
}: Props) {
  const [options, setOptions] = useState<TopicPromotion[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    let active = true
    const controller = new AbortController()
    const timer = window.setTimeout(
      async () => {
        setLoading(true)
        setLoadError('')
        try {
          const result = await searchTopicPromotions(topicId, search, listOnly, controller.signal)
          if (active) setOptions(result)
        } catch (error) {
          if (active) setLoadError((error as Error).message)
        } finally {
          if (active) setLoading(false)
        }
      },
      search ? 300 : 0
    )
    return () => {
      active = false
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [topicId, search, listOnly, retry])

  const mergedOptions = useMemo(() => {
    const byId = new Map(value.map((promotion) => [promotion.id, promotion]))
    options.forEach((promotion) => byId.set(promotion.id, promotion))
    return Array.from(byId.values())
  }, [options, value])

  return (
    <Box sx={{minWidth: 0}}>
      <Autocomplete<TopicPromotion, boolean, false, false>
        multiple={multiple}
        renderTags={() => null}
        sx={{
          minWidth: 0,
          '& .MuiAutocomplete-inputRoot': {minWidth: 0},
          '& .MuiAutocomplete-input': {minWidth: '60px !important'},
        }}
        disableCloseOnSelect={multiple}
        disabled={disabled}
        size='small'
        options={mergedOptions}
        value={multiple ? value : value[0] || null}
        loading={loading}
        getOptionDisabled={(promotion) => disallowSuggestedList && promotion.mechanicId === 4}
        filterOptions={(items) => items}
        getOptionLabel={(promotion) => promotion.name}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        onChange={(_event, selected) => {
          onChange(Array.isArray(selected) ? selected : selected ? [selected] : [])
        }}
        onInputChange={(_event, input, reason) => {
          if (reason === 'input' || reason === 'clear') setSearch(input)
        }}
        noOptionsText='Nenhuma promoção encontrada.'
        loadingText='Carregando promoções...'
        renderOption={(props, promotion, {selected}) => (
          <li {...props} key={promotion.id}>
            {multiple && (
              <Checkbox
                checked={selected}
                size='small'
                tabIndex={-1}
                disableRipple
                inputProps={{'aria-hidden': true}}
                sx={{mr: 1, flexShrink: 0, pointerEvents: 'none'}}
              />
            )}
            <Box sx={{minWidth: 0, py: 0.5}}>
              <Typography variant='body2' fontWeight={500} sx={{overflowWrap: 'anywhere'}}>
                {promotion.name}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                ID: {promotion.promotionId || promotion.id} · {promotion.cardType}
              </Typography>
              {disallowSuggestedList && promotion.mechanicId === 4 && (
                <Typography variant='caption' sx={{display: 'block', color: 'text.secondary'}}>
                  Lista sugerida indisponível para este tópico.
                </Typography>
              )}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            placeholder='Buscar por nome ou ID...'
            error={!!error}
            helperText={
              error ||
              (listOnly
                ? 'Selecione uma lista original cadastrada.'
                : 'Selecione promoções já cadastradas.')
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color='inherit' size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {loadError && (
        <Alert
          severity='error'
          sx={{mt: 1.5}}
          action={
            <Button color='inherit' size='small' onClick={() => setRetry((current) => current + 1)}>
              Tentar novamente
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}
      {value.length > 0 && (
        <Typography variant='caption' color='text.secondary' sx={{display: 'block', mt: 2, mb: 1}}>
          {value.length}{' '}
          {listOnly
            ? 'lista selecionada'
            : value.length === 1
            ? 'promoção selecionada'
            : 'promoções selecionadas'}
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            md: value.length > 1 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
          },
          gap: 1.5,
          minWidth: 0,
        }}
      >
        {value.map((promotion) => (
          <Box
            key={promotion.id}
            sx={{
              position: 'relative',
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              minWidth: 0,
              borderRadius: 2,
              bgcolor: '#f8fafc',
            }}
          >
            <IconButton
              aria-label={'Remover promoção ' + promotion.name}
              size='small'
              disabled={disabled}
              onClick={() => onChange(value.filter((item) => item.id !== promotion.id))}
              sx={{position: 'absolute', right: 8, top: 8}}
            >
              <X size={16} />
            </IconButton>
            <PromotionSummary promotion={promotion} />
          </Box>
        ))}
      </Box>
    </Box>
  )
}
